import axios from 'axios'
import { MouseEventHandler, createRef, useContext, useEffect, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { Avatar } from '@proj-client/components/participants/avatar'
import { UserSettings } from '@proj-client/components/user/userSettings'
import { useEventStore } from '@proj-client/stores/eventStore'
import { useUserDataStore } from '@proj-client/stores/userDataStore'
import { useEventModalsStore } from '@proj-client/stores/eventModalsStore'
import { UserSubscription } from './userSubscription'

interface IProps {
    color?: string,
}

export const UserMenu = (props: IProps) => {
    const event = useEventStore(store => store.event)
    const userData = useUserDataStore()
    const setPassphraseModal = useEventModalsStore(store => store.setPassphraseModal)

    const [isProfileMenuActive, setIsProfileMenuActive] = useState(false)
    const [avatarColor, setAvatarColor] = useState(props.color || 'yellow-300')

    const [settingsModalOpen, setSettingsModalOpen] = useState(false)
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)

    const menuRef = useRef<HTMLDivElement>()

    const toggleUserMenu: MouseEventHandler<HTMLButtonElement> = (e) => {
        e?.stopPropagation()
        setIsProfileMenuActive(!isProfileMenuActive)
    }

    useEffect(() => {
        const handleClick = (event) => {
            const withinMenuBoundaries = event.composedPath().includes(menuRef.current)
            if (!withinMenuBoundaries && isProfileMenuActive) {
                toggleUserMenu(null)
            }
        }

        return () => {
            document.removeEventListener('click', handleClick, false)
        }
    }, [event, menuRef, isProfileMenuActive])

    const manageSubscription = async () => {
        let resp = await axios.get('/api/stripe/customer_portal')
        //console.log(resp.data)
        window.location.replace(resp.data.redirect)
    }


    if (userData == null || userData == null || userData.user_type == 'visitor' || userData.user_type == 'unactivated') { return null }

    return (
        <>
            <div className='hidden md:inline relative content-center'>
                <button
                    onClick={toggleUserMenu}
                    className="flex flex-wrap content-center text-sm origin-top-right focus:outline idtn-btn" aria-haspopup="true">
                    <span className="sr-only">Open user menu</span>
                    <Avatar
                        name={userData.user_name || userData.session_name}
                        color={avatarColor ? avatarColor.substring(0, avatarColor.length - 3) : null}
                    />
                </button>

                <CSSTransition
                    nodeRef={menuRef}
                    in={isProfileMenuActive}
                    timeout={300}
                    classNames="profileMenuEl"
                    onEnter={() => null}
                    onExited={() => null}
                    unmountOnExit
                >
                    <div
                        id="user-menu"
                        ref={menuRef}
                        className="absolute z-50 w-48 py-1 transition origin-top-right transform scale-95 bg-white rounded-md shadow-lg opacity-0 top-11 right-0 ring-1 ring-black ring-opacity-10" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                        <div className="px-2 py-2 font-bold text-gray-700 text-md font-display ">{userData.user_name}</div>
                        {event?.id && !event?.isHost && (
                            <div className="flex flex-row items-center px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100" role="menuitem" onClick={() => {
                                setPassphraseModal({ isOpen: true })
                                toggleUserMenu(undefined)
                            }}>
                                <i className="mr-1 fa-solid fa-share"></i><span className={`flex-grow`}>Show passphrase</span>
                            </div>
                        )}

                        <div className="flex flex-row items-baseline px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100" role="menuitem" onClick={() => {
                            setSettingsModalOpen(true)
                            toggleUserMenu(undefined)
                        }}>
                            <i className="mr-1 fa-solid fa-gear"></i><span className="flex-grow">{userData.user_id ? 'Account Settings' : 'Settings'}</span>
                        </div>
                        <div className="flex flex-row items-baseline px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100" role="menuitem" onClick={() => {
                            setSubscriptionModalOpen(true)
                            toggleUserMenu(undefined)
                        }}>
                            <i className="mr-1 fa-solid fa-credit-card"></i><span className="flex-grow">Manage Subscription</span>
                        </div>
                        <a href="/logout" className="flex flex-row items-baseline px-2 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">
                            <i className="mr-1 fa-solid fa-arrow-right-from-bracket"></i><span className="flex-grow">Sign out</span>
                        </a>
                    </div>
                </CSSTransition>
            </div>


            <div id="user-menu-mobile" className="md:hidden grid grid-cols-1 justify-self-end" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                <div className="py-1 pr-1 my-1 text-base text-right">{userData.user_name}</div>
                {event?.id && !event?.isHost && (
                    <div className="flex flex-row items-center px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100" role="menuitem" onClick={() => {
                        setPassphraseModal({ isOpen: true })
                        toggleUserMenu(undefined)
                    }}>
                        <i className="mr-1 fa-solid fa-share"></i><span className={`flex-grow`}>Show passphrase</span>
                    </div>
                )}

                <div className="py-1 pr-1 my-1 font-base text-right" role="menuitem" onClick={() => {
                    setSettingsModalOpen(true)
                    toggleUserMenu(undefined)
                }}>
                    {userData.user_id ? 'Account Settings' : 'Settings'}
                </div>
                <div className="py-1 pr-1 my-1 font-base text-right" role="menuitem" onClick={() => {
                    setSubscriptionModalOpen(true)
                    toggleUserMenu(undefined)
                }}>
                    Manage Subscription
                </div>
                <a href="/logout" className="py-1 pr-1 my-1 font-base text-right" role="menuitem">
                    Sign out
                </a>
            </div>

            <UserSettings settingsModalOpen={settingsModalOpen} closeSettingsModal={() => setSettingsModalOpen(false)} />
            <UserSubscription subscriptionModalOpen={subscriptionModalOpen} closeSubscriptionModal={() => setSubscriptionModalOpen(false)} />
        </>
    )
}